import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, getAuthHeaders, getRoleLabel, GUARD_PERMISSIONS } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ShieldCheck,
  Plus,
  Pencil,
  RotateCcw,
  Trash2,
  Footprints,
  LayoutDashboard,
  Ban,
} from "lucide-react";

const GLOBAL = "global";

/**
 * Página "Roles y permisos".
 *
 * - Lista los roles del sistema (super_admin, admin, revendedor, supervisor, operador,
 *   vigilante) con sus permisos efectivos para la compañía seleccionada, más los roles
 *   personalizados creados por la compañía.
 * - Permite ajustar los permisos de un rol del sistema (se guarda como ajuste de la
 *   compañía y se puede restablecer) y crear/editar/eliminar roles personalizados.
 * - Quien no es super administrador solo puede otorgar permisos que él mismo tiene.
 */
export default function Roles() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { user, can, isSuperAdmin, canManageCompanies } = useAuth();

  const [catalog, setCatalog] = useState({ groups: [], all: [] });
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(isSuperAdmin() ? GLOBAL : user?.company_id || "");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rol que se edita, o null para crear
  const [form, setForm] = useState({ name: "", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: "reset" | "delete", role }

  const companyQuery = useMemo(
    () => (companyId && companyId !== GLOBAL ? `?company_id=${companyId}` : ""),
    [companyId]
  );

  const permLabel = (perm) =>
    t(`permissions.labels.${perm.replace(/\./g, "_")}`, { defaultValue: perm });
  const groupLabel = (key) => t(`permissions.groups.${key}`, { defaultValue: key });
  const roleName = (role) => getRoleLabel(t, role.key, role.name);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const requests = [
        axios.get(`${API_URL}/permissions`, { headers }),
        axios.get(`${API_URL}/roles${companyQuery}`, { headers }),
      ];
      if (canManageCompanies()) {
        requests.push(axios.get(`${API_URL}/companies`, { headers }));
      }
      const [permRes, rolesRes, companiesRes] = await Promise.all(requests);
      setCatalog(permRes.data || { groups: [], all: [] });
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      if (companiesRes) {
        setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "pages.roles.loadError"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyQuery]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ---- Clasificación de acceso según permisos ----
  const accessKind = (perms) => {
    if (!perms || perms.length === 0) return "none";
    return perms.some((p) => !GUARD_PERMISSIONS.includes(p)) ? "panel" : "patrol";
  };

  const AccessBadge = ({ perms }) => {
    const kind = accessKind(perms);
    if (kind === "panel") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          <LayoutDashboard className="h-3 w-3" /> {t("pages.roles.panelAccess")}
        </span>
      );
    }
    if (kind === "patrol") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <Footprints className="h-3 w-3" /> {t("pages.roles.patrolAccess")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
        <Ban className="h-3 w-3" /> {t("pages.roles.noAccess")}
      </span>
    );
  };

  // ---- Diálogo crear / editar ----
  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", permissions: [...GUARD_PERMISSIONS] });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name || "", permissions: [...(role.permissions || [])] });
    setDialogOpen(true);
  };

  // Un usuario que no es super admin solo puede otorgar permisos que él tiene
  const canGrant = (perm) => isSuperAdmin() || can(perm);

  const togglePerm = (perm, checked) => {
    setForm((prev) => {
      const set = new Set(prev.permissions);
      if (checked) set.add(perm);
      else set.delete(perm);
      return { ...prev, permissions: catalog.all.filter((p) => set.has(p)) };
    });
  };

  const setGroup = (group, checked) => {
    setForm((prev) => {
      const set = new Set(prev.permissions);
      group.permissions.forEach((perm) => {
        if (!canGrant(perm)) return;
        if (checked) set.add(perm);
        else set.delete(perm);
      });
      return { ...prev, permissions: catalog.all.filter((p) => set.has(p)) };
    });
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error(t("pages.roles.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        name,
        permissions: form.permissions,
        company_id: companyId && companyId !== GLOBAL ? companyId : null,
      };
      if (editing) {
        await axios.put(`${API_URL}/roles/${editing.key}`, payload, { headers });
        toast.success(t("pages.roles.saved"));
      } else {
        await axios.post(`${API_URL}/roles`, payload, { headers });
        toast.success(t("pages.roles.created"));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "pages.roles.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { type, role } = confirm;
    try {
      await axios.delete(`${API_URL}/roles/${role.key}${companyQuery}`, { headers: getAuthHeaders() });
      toast.success(type === "reset" ? t("pages.roles.resetDone") : t("pages.roles.deleted"));
      setConfirm(null);
      await fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "pages.roles.saveError"));
    }
  };

  if (!can("roles.manage")) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">{t("pages.roles.noPermission")}</p>
      </div>
    );
  }

  const formAccess = accessKind(form.permissions);

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" />
            {t("pages.roles.title")}
          </h1>
          <p className="text-slate-500 mt-1">{t("pages.roles.subtitle")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {canManageCompanies() && (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-full sm:w-[260px]" data-testid="roles-company-select">
                <SelectValue placeholder={t("pages.roles.scope")} />
              </SelectTrigger>
              <SelectContent>
                {isSuperAdmin() && <SelectItem value={GLOBAL}>{t("pages.roles.global")}</SelectItem>}
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={openCreate} className="gap-2" data-testid="new-role-btn">
            <Plus className="h-4 w-4" />
            {t("pages.roles.newRole")}
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-500">{t("pages.roles.superAdminNote")}</p>

      {/* Tabla de roles */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t("common.loading")}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.roles.role")}</TableHead>
                  <TableHead>{t("pages.roles.type")}</TableHead>
                  <TableHead>{t("pages.roles.access")}</TableHead>
                  <TableHead>{t("pages.roles.permissions")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.key} data-testid={`role-row-${role.key}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{roleName(role)}</div>
                      <div className="text-xs text-slate-400 font-mono">{role.key}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {role.is_system ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                            {t("pages.roles.system")}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                            {t("pages.roles.custom")}
                          </span>
                        )}
                        {role.is_system && role.overridden && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            {t("pages.roles.customized")}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <AccessBadge perms={role.permissions} />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-700">
                        {t("pages.roles.count", { count: role.permissions.length })}
                      </span>
                      <span className="text-xs text-slate-400"> / {catalog.all.length}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {role.editable && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(role)}
                            title={t("pages.roles.edit")}
                            data-testid={`edit-role-${role.key}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {role.editable && role.is_system && role.overridden && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirm({ type: "reset", role })}
                            title={t("pages.roles.reset")}
                            data-testid={`reset-role-${role.key}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        {role.editable && !role.is_system && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setConfirm({ type: "delete", role })}
                            title={t("pages.roles.delete")}
                            data-testid={`delete-role-${role.key}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Diálogo crear / editar rol */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("pages.roles.editTitle", { name: roleName(editing) })
                : t("pages.roles.createTitle")}
            </DialogTitle>
            <DialogDescription>
              {formAccess === "panel" && t("pages.roles.hintPanel")}
              {formAccess === "patrol" && t("pages.roles.hintPatrolOnly")}
              {formAccess === "none" && t("pages.roles.hintNone")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label>{t("pages.roles.name")} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("pages.roles.namePlaceholder")}
                data-testid="role-name-input"
              />
              {editing && (
                <p className="text-xs text-slate-400">
                  {t("pages.roles.keyLabel")}: <span className="font-mono">{editing.key}</span>
                </p>
              )}
            </div>

            <div className="space-y-4">
              {catalog.groups.map((group) => {
                const grantable = group.permissions.filter(canGrant);
                const allChecked =
                  grantable.length > 0 && grantable.every((p) => form.permissions.includes(p));
                return (
                  <div key={group.key} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-800">{groupLabel(group.key)}</h3>
                      {grantable.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => setGroup(group, !allChecked)}
                        >
                          {allChecked ? t("pages.roles.clearAll") : t("pages.roles.selectAll")}
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.permissions.map((perm) => {
                        const disabled = !canGrant(perm);
                        return (
                          <label
                            key={perm}
                            className={`flex items-start gap-2 text-sm rounded-md px-2 py-1.5 ${
                              disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50 cursor-pointer"
                            }`}
                            title={disabled ? t("pages.roles.notOwned") : undefined}
                          >
                            <Checkbox
                              checked={form.permissions.includes(perm)}
                              disabled={disabled}
                              onCheckedChange={(checked) => togglePerm(perm, checked === true)}
                              data-testid={`perm-${perm}`}
                            />
                            <span>
                              <span className="text-slate-800">{permLabel(perm)}</span>
                              <span className="block text-xs text-slate-400 font-mono">{perm}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-role-btn">
              {saving ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación restablecer / eliminar */}
      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "reset" ? t("pages.roles.resetTitle") : t("pages.roles.deleteTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "reset"
                ? t("pages.roles.resetDescription", { name: confirm ? roleName(confirm.role) : "" })
                : t("pages.roles.deleteDescription", { name: confirm ? roleName(confirm.role) : "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={confirm?.type === "delete" ? "bg-red-600 hover:bg-red-700" : undefined}
            >
              {confirm?.type === "reset" ? t("pages.roles.reset") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
